import React, { useMemo } from 'react';
import { GridColDef, GridDensity, GridRowId } from '@mui/x-data-grid-premium';
import { ConditionsNew } from '@retail/calculus/types';
import { formatPrice } from '@retail/products/utils';
import { DATA_GRID_ID } from '@retail/utils';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { useTranslation } from 'react-i18next';
import { baseColumnProps, resolveCondition } from '../AllConditions/utils';
import {
  ConditionsTreeDataGrid,
  conditionsTreeDataGridInputCellClass,
} from '../ConditionsTreeDataGrid';
import {
  buildDiscountMatrix,
  DiscountMatrixEdits,
  getDiscountMatrixCellKey,
  getDiscountMatrixCellState,
  getDiscountMatrixRowId,
} from './discountMatrixUtils';
import { DiscountValueCell } from './DiscountValueCell';
import { DiscountMatrixInputCell } from './DiscountMatrixInputCell';

interface Props {
  conditions: ConditionsNew[];
  orgUnitName: string;
  loading?: boolean;
  density?: GridDensity;
  showDates?: boolean;
  editMode?: boolean;
  edits?: DiscountMatrixEdits;
  onEdit?: (cellKey: string, value: string) => void;
}

export function DiscountMatrixTable({
  conditions,
  orgUnitName,
  loading,
  density,
  showDates = false,
  editMode = false,
  edits = {},
  onEdit,
}: Props) {
  const t = useCalculusTFunction();
  const { i18n } = useTranslation();

  const { rows, customers, cells } = useMemo(() => buildDiscountMatrix(conditions), [conditions]);

  const columns: GridColDef<ConditionsNew>[] = useMemo(
    () =>
      customers.flatMap(({ assortmentId, assortmentName }): GridColDef<ConditionsNew>[] => [
        {
          ...baseColumnProps,
          headerAlign: 'center',
          align: 'center',
          field: `discount:${assortmentId}`,
          headerName: assortmentName,
          width: 170,
          cellClassName: editMode ? conditionsTreeDataGridInputCellClass : undefined,
          renderCell: (params) => {
            const condition = resolveCondition(params);
            if (!condition) return null;
            const rowId = getDiscountMatrixRowId(condition);
            const value = cells.get(rowId)?.get(assortmentId);

            if (editMode) {
              const cellKey = getDiscountMatrixCellKey(rowId, assortmentId);
              const original = value?.discountPercent ?? undefined;
              const edit = edits[cellKey];
              const { invalid, changed } = getDiscountMatrixCellState(original, edit);
              const isRowHighlighted = (otherRowId: GridRowId) => {
                const otherRowKey = String(otherRowId);
                const state = getDiscountMatrixCellState(
                  cells.get(otherRowKey)?.get(assortmentId)?.discountPercent ?? undefined,
                  edits[getDiscountMatrixCellKey(otherRowKey, assortmentId)]
                );
                return state.invalid || state.changed;
              };
              return (
                <DiscountMatrixInputCell
                  rowId={params.id}
                  field={params.field}
                  value={
                    edit ?? (original !== undefined ? formatPrice(original, i18n.language) : '')
                  }
                  tabIndex={params.tabIndex}
                  changed={changed}
                  invalid={invalid}
                  isRowHighlighted={isRowHighlighted}
                  onChange={(newValue) => onEdit?.(cellKey, newValue)}
                />
              );
            }

            if (value?.discountPercent === undefined) return '-';
            return (
              <DiscountValueCell
                value={`${formatPrice(value.discountPercent, i18n.language)} %`}
                validFrom={value.validFrom}
                validTo={value.validTo}
                showDates={showDates}
              />
            );
          },
        },
      ]),
    [cells, customers, editMode, edits, i18n.language, onEdit, showDates]
  );

  return (
    <ConditionsTreeDataGrid
      dataGridId={DATA_GRID_ID.CALCULUS_DISCOUNT_MATRIX_TABLE}
      rows={rows}
      getRowId={getDiscountMatrixRowId}
      columns={columns}
      language={i18n.language}
      loading={loading}
      density={density}
      noRowsText={t('calculus.noCustomerMarkup')}
      getAssortmentLabel={() => orgUnitName}
    />
  );
}
